import { Check } from 'lucide-react-native';
import { Image, Pressable, StyleSheet, View } from 'react-native';

import { Avatar } from '@/components/Avatar';
import { BrandLogo } from '@/components/BrandLogo';
import { Heading } from '@/components/Heading';
import { Pill } from '@/components/Pill';
import { Text } from '@/components/Text';
import { colors, fontSizes, radii, spacing } from '@/constants/theme';
import { formatMoney } from '@/lib/money';
import type { VoltrBrand, VoltrProduct } from '@/types/voltr';

export type OrderProductStepProps = {
  brand: VoltrBrand;
  /** The deal's own logo, as on the deal page; Voltr's when the deal has none. */
  logoUrl?: string | null;
  selectedId: string | null;
  onSelect: (productId: string) => void;
};

/**
 * Order step 1 (Figma "Order 1/2"): the brand, then one card per product it
 * will ship. Which can be picked is Voltr's call (`can_request`, each
 * product's `available`); a brand that isn't taking orders says why.
 */
export function OrderProductStep({ brand, logoUrl, selectedId, onSelect }: OrderProductStepProps) {
  return (
    <>
      <View style={styles.brand}>
        <BrandLogo
          url={logoUrl ?? brand.logo_url}
          size={LOGO}
          radius={radii.base}
          inset={6}
          fallback={<Avatar initials={brand.name.slice(0, 2).toUpperCase()} size={LOGO} />}
        />
        <Heading size="3xl" style={styles.brandName}>
          {brand.name}
        </Heading>
      </View>

      {!brand.can_request ? (
        <Text color="textMuted" testID="voltr-order-unavailable">
          {brand.unavailable_reason ?? `${brand.name} isn't taking orders right now.`}
        </Text>
      ) : null}

      <View style={styles.list} testID="voltr-products">
        {brand.products.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            selected={product.id === selectedId}
            disabled={!brand.can_request || !product.available}
            onPress={() => onSelect(product.id)}
          />
        ))}
      </View>
    </>
  );
}

type ProductCardProps = { product: VoltrProduct; selected: boolean; disabled: boolean; onPress: () => void };

function ProductCard({ product, selected, disabled, onPress }: ProductCardProps) {
  const value = product.price_cents != null ? `${formatMoney(product.price_cents)} Value` : null;
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="radio"
      accessibilityState={{ selected, disabled }}
      accessibilityLabel={[product.title, 'free', value, disabled ? 'unavailable' : null].filter(Boolean).join(', ')}
      style={({ pressed }) => [styles.card, selected && styles.cardSelected, disabled && styles.cardDisabled, pressed && styles.pressed]}
      testID={`voltr-product-${product.id}`}
    >
      <View style={styles.photo}>
        {product.image_url ? <Image source={{ uri: product.image_url }} style={styles.image} resizeMode="contain" /> : null}
        <Pill tone="green" style={styles.free}>
          Free
        </Pill>
        {selected ? (
          <View style={styles.check} testID={`voltr-product-${product.id}-check`}>
            <Check size={16} color={colors.frost} strokeWidth={3} />
          </View>
        ) : null}
      </View>
      <Text variant="bodyBold" color="deepGreen" numberOfLines={2} style={styles.title}>
        {product.title}
      </Text>
      {value ? (
        <Text variant="caption" color="textMuted">
          {value}
        </Text>
      ) : null}
    </Pressable>
  );
}

const LOGO = 52;
const CHECK = 28;

const styles = StyleSheet.create({
  brand: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  brandName: { flex: 1 },
  list: { gap: spacing.base },
  card: {
    gap: spacing.xs,
    padding: spacing.md,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    borderWidth: 2,
    borderColor: colors.border,
  },
  cardSelected: { borderColor: colors.electricGreen },
  cardDisabled: { opacity: 0.5 },
  pressed: { opacity: 0.8 },
  // Full card width: with aspectRatio alone it sized narrower, leaving a wide right edge.
  photo: {
    width: '100%',
    aspectRatio: 16 / 9,
    marginBottom: spacing.sm,
    borderRadius: radii.base,
    overflow: 'hidden',
    backgroundColor: colors.surfaceSunken,
  },
  image: { width: '100%', height: '100%' },
  free: { position: 'absolute', top: spacing.sm, left: spacing.sm },
  check: {
    position: 'absolute',
    top: spacing.sm,
    right: spacing.sm,
    width: CHECK,
    height: CHECK,
    borderRadius: radii.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.electricGreen,
  },
  title: { fontSize: fontSizes.lg },
});
